var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) if (e.indexOf(p[i]) < 0)
            t[p[i]] = s[p[i]];
    return t;
};
import * as React from 'react';
import * as PropTypes from 'prop-types';
import * as _ from 'lodash';
import Downshift from 'downshift';
import { AutoControlledComponent, customPropTypes, commonPropTypes, } from '../../lib';
import keyboardKey from 'keyboard-key';
import List from '../List/List';
import Text from '../Text/Text';
import Icon from '../Icon/Icon';
import Ref from '../Ref/Ref';
import DropdownItem from './DropdownItem';
import DropdownLabel from './DropdownLabel';
import DropdownSearchInput from './DropdownSearchInput';
/**
 * Dropdown allows user to select one or more values from a list of items.
 * Can also be created with search capability.
 * @accessibility
 * Implements ARIA collapsible Listbox design pattern, uses aria-live to announce state changes.
 */
var Dropdown = /** @class */ (function (_super) {
    __extends(Dropdown, _super);
    function Dropdown() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.state = {
            // prevent deletion of last character + last selected value at the same time on backspace.
            backspaceDelete: _this.props.multiple,
            focused: false,
            searchQuery: _this.props.search ? '' : undefined,
            value: _this.props.multiple ? [] : null,
        };
        _this.handleDownshiftStateChanges = function (state, changes) {
            switch (changes.type) {
                case Downshift.stateChangeTypes.changeInput:
                    _this.trySetState({
                        searchQuery: changes.inputValue,
                    });
                    _this.setState({
                        backspaceDelete: !(state.inputValue.length > 0 && changes.inputValue.length === 0),
                    });
                    _.invoke(_this.props, 'onSearchQueryChange', {}, __assign({}, _this.props, { searchQuery: changes.inputValue }));
                    return changes;
                default:
                    return changes;
            }
        };
        _this.getItemsFilteredBySearchQuery = function (items) {
            var _a = _this.props, itemToString = _a.itemToString, multiple = _a.multiple, search = _a.search;
            var _b = _this.state, searchQuery = _b.searchQuery, value = _b.value;
            var nonSelectedItems = items.filter(function (item) {
                return multiple ? value.indexOf(item) === -1 : true;
            });
            var itemsMatchSearchQuery = nonSelectedItems.filter(function (item) {
                return search
                    ? _.isFunction(search)
                        ? search(itemsMatchSearchQuery, searchQuery)
                        : itemToString(item)
                            .toLowerCase()
                            .indexOf(searchQuery.toLowerCase()) !== -1
                    : true;
            });
            return itemsMatchSearchQuery;
        };
        _this.setA11yStatus = function (statusMessage) {
            var elementId = 'stardust-dropdown-a11y-status';
            var statusDiv = document.getElementById(elementId);
            if (!statusDiv) {
                statusDiv = document.createElement('div');
                statusDiv.setAttribute('id', elementId);
                statusDiv.setAttribute('role', 'status');
                statusDiv.setAttribute('aria-live', 'polite');
                statusDiv.setAttribute('aria-relevant', 'additions text');
                Object.assign(statusDiv.style, {
                    border: '0',
                    clip: 'rect(0 0 0 0)',
                    height: '1px',
                    margin: '-1px',
                    overflow: 'hidden',
                    padding: '0',
                    position: 'absolute',
                    width: '1px',
                });
                document.body.appendChild(statusDiv);
            }
            statusDiv.textContent = statusMessage;
        };
        _this.handleItemOverrides = function (item, index, getItemProps) { return ({
            accessibilityItemProps: getItemProps({ item: item, index: index }),
        }); };
        _this.handleSelectedItemOverrides = function (predefinedProps, item) { return ({
            onRemove: function (e, dropdownLabelProps) {
                _this.handleSelectedItemRemove(e, item);
                _.invoke(predefinedProps, 'onRemove', e, dropdownLabelProps);
            },
            onClick: function (e, dropdownLabelProps) {
                e.stopPropagation();
                _.invoke(predefinedProps, 'onClick', e, dropdownLabelProps);
            },
        }); };
        _this.handleSearchInputOverrides = function (predefinedProps, highlightedIndex, selectItemAtIndex, getRootProps, getInputProps) {
            var handleInputBlur = function (e, searchInputProps) {
                _this.setState({ focused: false });
                _.invoke(predefinedProps, 'onInputBlur', e, searchInputProps);
            };
            var handleInputKeyDown = function (e, searchInputProps) {
                if (keyboardKey.getCode(e) === keyboardKey.Tab && highlightedIndex !== undefined) {
                    selectItemAtIndex(highlightedIndex);
                }
                _.invoke(predefinedProps, 'onInputKeyDown', e, __assign({}, searchInputProps, { highlightedIndex: highlightedIndex,
                    selectItemAtIndex: selectItemAtIndex }));
            };
            return __assign({ 
                // getInputProps adds Downshift handlers. We also add our own by passing them as params to that function.
                // user handlers were also added to our handlers previously, at the beginning of this function.
                accessibilityInputProps: __assign({}, getInputProps({
                    onBlur: function (e) {
                        handleInputBlur(e, predefinedProps);
                    },
                    onKeyDown: function (e) {
                        handleInputKeyDown(e, predefinedProps);
                    },
                })), 
                // same story as above for getRootProps.
                accessibilityWrapperProps: __assign({}, getRootProps({ refKey: 'innerRef' }, { suppressRefError: true })), onFocus: function (e, searchInputProps) {
                    _this.setState({ focused: true });
                    _.invoke(predefinedProps, 'onFocus', e, searchInputProps);
                } }, (_this.props.multiple && {
                onKeyUp: function (e, searchInputProps) {
                    if (keyboardKey.getCode(e) === keyboardKey.Backspace) {
                        var _a = _this.state, searchQuery = _a.searchQuery, value = _a.value, backspaceDelete = _a.backspaceDelete;
                        if (searchQuery === '' && value.length > 0) {
                            if (backspaceDelete) {
                                _this.removeItemFromValue();
                            }
                            else {
                                _this.setState({ backspaceDelete: true });
                            }
                        }
                    }
                    _.invoke(predefinedProps, 'onKeyUp', e, searchInputProps);
                },
            }), { onInputBlur: function (e, searchInputProps) {
                    handleInputBlur(e, searchInputProps);
                }, onInputKeyDown: function (e, searchInputProps) {
                    handleInputKeyDown(e, searchInputProps);
                } });
        };
        _this.handleContainerClick = function (isOpen) {
            !isOpen && _this.inputNode.focus();
        };
        _this.handleSelectedChange = function (item) {
            var _a = _this.props, multiple = _a.multiple, getA11ySelectionMessage = _a.getA11ySelectionMessage;
            var newValue = multiple ? _this.state.value.concat([item]) : item;
            _this.trySetState({
                value: newValue,
                searchQuery: '',
            });
            if (getA11ySelectionMessage && getA11ySelectionMessage.onAdd) {
                _this.setA11yStatus(getA11ySelectionMessage.onAdd(item));
            }
            // we don't have event for it, but want to keep the event handling interface, event is empty.
            _.invoke(_this.props, 'onSelectedChange', {}, __assign({}, _this.props, { value: newValue }));
        };
        return _this;
    }
    Dropdown.prototype.renderComponent = function (_a) {
        var _this = this;
        var ElementType = _a.ElementType, classes = _a.classes, styles = _a.styles, variables = _a.variables, rest = _a.rest;
        var _b = this.props, search = _b.search, multiple = _b.multiple, toggleButton = _b.toggleButton, getA11yStatusMessage = _b.getA11yStatusMessage, itemToString = _b.itemToString;
        var searchQuery = this.state.searchQuery;
        return (React.createElement(ElementType, __assign({ className: classes.root }, rest),
            React.createElement(Downshift, { onChange: this.handleSelectedChange, inputValue: searchQuery, stateReducer: this.handleDownshiftStateChanges, itemToString: itemToString, 
                // Downshift does not support multiple selection. We will handle everything and pass it selected as null in this case.
                selectedItem: multiple ? null : undefined, getA11yStatusMessage: getA11yStatusMessage }, function (_a) {
                var getInputProps = _a.getInputProps, getItemProps = _a.getItemProps, getMenuProps = _a.getMenuProps, getRootProps = _a.getRootProps, getToggleButtonProps = _a.getToggleButtonProps, isOpen = _a.isOpen, highlightedIndex = _a.highlightedIndex, selectItemAtIndex = _a.selectItemAtIndex;
                return (React.createElement("div", { className: classes.containerDiv, onClick: _this.handleContainerClick.bind(_this, isOpen) },
                    multiple && _this.renderSelectedItems(styles),
                    search &&
                        _this.renderSearchInput(getRootProps, getInputProps, highlightedIndex, selectItemAtIndex),
                    toggleButton && _this.renderToggleButton(getToggleButtonProps, styles, isOpen),
                    _this.renderItemsList(styles, variables, getMenuProps, getItemProps, isOpen, highlightedIndex)));
            })));
    };
    Dropdown.prototype.renderSearchInput = function (getRootProps, getInputProps, highlightedIndex, selectItemAtIndex) {
        var _this = this;
        var _a = this.props, searchInput = _a.searchInput, multiple = _a.multiple, placeholder = _a.placeholder;
        var _b = this.state, searchQuery = _b.searchQuery, value = _b.value;
        var noPlaceholder = searchQuery.length > 0 || (multiple && value.length > 0);
        return DropdownSearchInput.create(searchInput || {}, {
            defaultProps: {
                placeholder: noPlaceholder ? '' : placeholder,
                inputRef: function (inputNode) {
                    _this.inputNode = inputNode;
                },
            },
            overrideProps: function (predefinedProps) {
                return _this.handleSearchInputOverrides(predefinedProps, highlightedIndex, selectItemAtIndex, getRootProps, getInputProps);
            },
        });
    };
    Dropdown.prototype.renderToggleButton = function (getToggleButtonProps, styles, isOpen) {
        return (React.createElement(Icon, __assign({ name: "chevron " + (isOpen ? 'up' : 'down'), as: "button", tabIndex: "-1", styles: styles.toggleButton }, getToggleButtonProps())));
    };
    Dropdown.prototype.renderItemsList = function (styles, variables, getMenuProps, getItemProps, isOpen, highlightedIndex) {
        var accessibilityMenuProps = getMenuProps({ refKey: 'innerRef' });
        var innerRef = accessibilityMenuProps.innerRef, accessibilityMenuPropsRest = __rest(accessibilityMenuProps, ["innerRef"]);
        return (React.createElement(Ref, { innerRef: innerRef },
            React.createElement(List, __assign({}, accessibilityMenuPropsRest, { styles: styles.list, "aria-hidden": !isOpen, items: isOpen ? this.renderItems(styles, variables, getItemProps, highlightedIndex) : [] }))));
    };
    Dropdown.prototype.renderItems = function (styles, variables, getItemProps, highlightedIndex) {
        var _this = this;
        var _a = this.props, items = _a.items, noResultsMessage = _a.noResultsMessage;
        var filteredItems = this.getItemsFilteredBySearchQuery(items);
        if (filteredItems.length > 0) {
            return filteredItems.map(function (item, index) {
                var itemAsListItem = item;
                if (typeof item === 'object') {
                    itemAsListItem = _.pickBy(item, function (value, key) {
                        return _.includes(['key'].concat(DropdownItem.handledProps), key);
                    });
                }
                return DropdownItem.create(itemAsListItem, {
                    defaultProps: __assign({ active: highlightedIndex === index, variables: variables }, (typeof item === 'object' &&
                        !item.hasOwnProperty('key') && {
                        key: item.header,
                    })),
                    overrideProps: function (predefinedProps) {
                        return _this.handleItemOverrides(item, index, getItemProps);
                    },
                });
            });
        }
        // render no match message.
        return [
            noResultsMessage
                ? {
                    key: 'dropdown-no-results',
                    content: React.createElement(Text, { weight: "bold", content: noResultsMessage }),
                    styles: styles.emptyListItem,
                }
                : null,
        ];
    };
    Dropdown.prototype.renderSelectedItems = function (styles) {
        var _this = this;
        var value = this.state.value;
        if (value.length === 0) {
            return null;
        }
        return value.map(function (item) {
            return DropdownLabel.create(item, {
                defaultProps: __assign({ styles: styles.label }, (typeof item === 'object' &&
                    !item.hasOwnProperty('key') && {
                    key: item.header,
                })),
                overrideProps: function (predefinedProps) {
                    return _this.handleSelectedItemOverrides(predefinedProps, item);
                },
            });
        });
    };
    Dropdown.prototype.handleSelectedItemRemove = function (e, item) {
        this.removeItemFromValue(item);
        this.inputNode.focus();
        e.stopPropagation();
    };
    Dropdown.prototype.removeItemFromValue = function (item) {
        var getA11ySelectionMessage = this.props.getA11ySelectionMessage;
        var value = this.state.value;
        var poppedItem = item;
        if (poppedItem) {
            value = value.filter(function (currentElement) { return currentElement !== item; });
        }
        else {
            poppedItem = value.pop();
        }
        this.trySetState({
            value: value,
        });
        if (getA11ySelectionMessage && getA11ySelectionMessage.onRemove) {
            this.setA11yStatus(getA11ySelectionMessage.onRemove(item));
        }
        // we don't have event for it, but want to keep the event handling interface, event is empty.
        _.invoke(this.props, 'onSelectedChange', {}, __assign({}, this.props, { value: value }));
    };
    Dropdown.displayName = 'Dropdown';
    Dropdown.className = 'ui-dropdown';
    Dropdown.propTypes = __assign({}, commonPropTypes.createCommon({
        children: false,
        content: false,
    }), { defaultSearchQuery: PropTypes.string, defaultValue: PropTypes.oneOfType([
            customPropTypes.itemShorthand,
            customPropTypes.collectionShorthand,
        ]), fluid: PropTypes.bool, getA11ySelectionMessage: PropTypes.object, getA11yStatusMessage: PropTypes.func, items: customPropTypes.collectionShorthand, itemToString: PropTypes.func, multiple: PropTypes.bool, noResultsMessage: PropTypes.string, onSearchQueryChange: PropTypes.func, onSelectedChange: PropTypes.func, placeholder: PropTypes.string, search: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]), searchQuery: PropTypes.string, searchInput: customPropTypes.itemShorthand, toggleButton: PropTypes.bool, value: PropTypes.oneOfType([
            customPropTypes.itemShorthand,
            customPropTypes.collectionShorthand,
        ]) });
    Dropdown.defaultProps = {
        as: 'div',
        itemToString: function (item) {
            if (!item || React.isValidElement(item)) {
                return '';
            }
            // targets DropdownItem shorthand objects
            if (item.header) {
                return item.header;
            }
            return "" + item;
        },
    };
    Dropdown.autoControlledProps = ['searchQuery', 'value'];
    Dropdown.Item = DropdownItem;
    Dropdown.Label = DropdownLabel;
    Dropdown.SearchInput = DropdownSearchInput;
    return Dropdown;
}(AutoControlledComponent));
export default Dropdown;
